import React, {
  createContext,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import styles from './widget.module.scss';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import { hiddenWidgetsAtom } from 'stores/dashboard-store/dashboard-store';
import { WidgetHeader } from './widget-header/WidgetHeader';
import { WidgetContent } from './widget-content/WidgetContent';
import { WidgetContextType, WidgetConfig, WidgetState, WidgetType } from 'modules/dashboard/types';
import { getWidgetClassName } from 'modules/dashboard/utils/widget/getWidgetClassname';

export const WidgetContext = createContext<WidgetContextType | null>(null);

export interface WidgetProps extends React.HTMLAttributes<HTMLDivElement> {
  widgetConfig: WidgetConfig;
  isEditing?: boolean;
  className?: string;
  children?: React.ReactNode;
}

const Widget = forwardRef<HTMLDivElement, WidgetProps>(
  ({ widgetConfig, isEditing, className, children, ...rest }, ref) => {
    const { type, removeElements, hideMode, isScrollable } = widgetConfig;
    const [hiddenWidgets, setHiddenWidgets] = useAtom(hiddenWidgetsAtom);
    const isWidgetHidden = type ? hiddenWidgets.includes(type) : false;
    const containerRef = useRef<HTMLDivElement>(null);
    const [hasScrollbar, setHasScrollbar] = useState(false);
    const [isContentVisible, setContentVisible] = useState(true);
    const [widgetState, setWidgetState] = useState<WidgetState>(WidgetState.Success);
    const [isReloadDisabled, setisReloadDisabled] = useState(false);
    const [reloadButtonAction, setReloadButtonAction] = useState<(() => void) | null>(null);

    useImperativeHandle(ref, () => containerRef.current as HTMLDivElement);

    const widgetContextValue = useMemo(
      () => ({
        setReloadHandler: setReloadButtonAction,
        setWidgetState: setWidgetState
      }),
      []
    );

    // Check if a scrollbar is present
    useEffect(() => {
      const el = containerRef.current;
      if (!el) return;

      const checkScroll = () => {
        const isScrollable = el.scrollHeight > el.clientHeight;
        setHasScrollbar(isScrollable);
      };

      checkScroll();
    }, [widgetState]);

    const handleReload = () => {
      if (reloadButtonAction) {
        reloadButtonAction();
        setisReloadDisabled(true);
        setTimeout(() => setisReloadDisabled(false), 60000);
      }
    };

    const handleHideToggle = useCallback(() => {
      setContentVisible(false);
      setTimeout(() => {
        if (widgetConfig.type !== undefined) {
          setHiddenWidgets((prev) =>
            prev.includes(widgetConfig.type as WidgetType)
              ? prev.filter((w) => w !== (widgetConfig.type as WidgetType))
              : [...prev, widgetConfig.type as WidgetType]
          );
        }
        setContentVisible(true);
      }, 400);
    }, [widgetConfig.type, setHiddenWidgets]);

    const [hasMounted, setHasMounted] = useState(false);
    useEffect(() => {
      const timeout = setTimeout(() => setHasMounted(true), 50);
      return () => clearTimeout(timeout);
    }, []);

    if (removeElements) {
      return (
        <div
          ref={ref}
          {...rest}
          className={clsx(styles.widget, className, {
            ...(styles.fadeInUp && hasMounted ? { [styles.fadeInUp]: true } : {})
          })}
        >
          {children}
        </div>
      );
    }

    return (
      <WidgetContext.Provider value={widgetContextValue}>
        <div
          ref={ref}
          {...rest}
          className={getWidgetClassName(widgetConfig, className, hasMounted)}
        >
          <WidgetHeader
            widgetConfig={widgetConfig}
            isEditing={isEditing}
            widgetState={widgetState}
            isWidgetHidden={isWidgetHidden}
            onHideToggle={handleHideToggle}
            onReload={handleReload}
            isReloadDisabled={isReloadDisabled}
          />
          <WidgetContent
            ref={containerRef}
            type={type}
            hideMode={hideMode === 'widget' || hideMode === 'content' ? hideMode : undefined}
            isScrollable={isScrollable}
            hasScrollbar={hasScrollbar}
            isContentVisible={isContentVisible}
            isWidgetHidden={isWidgetHidden}
          >
            {children}
          </WidgetContent>
        </div>
      </WidgetContext.Provider>
    );
  }
);

export default Widget;
